import type { JsonSchemaType, MetadataGroup } from '@gmn-portal/backend';
import { AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { useForm } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import * as z from 'zod';
import { useIntl } from '@/core/i18n/use-intl';
import { cn } from '@/shared/utils';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../accordion';
import { FormField } from '../../form';
import { Label } from '../../label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../tabs';
import { DEFAULT_ZOD_HANDLERS, INPUT_COMPONENTS } from '../config';
import resolveDependencies from '../dependencies';
import type { Dependency, FieldConfig, FieldConfigItem } from '../types';
import {
  beautifyObjectName,
  getBaseSchema,
  getBaseType,
  getObjectFormSchema,
  zodToHtmlInputProps,
} from '../utils';
import AutoFormArray from './array';

function DefaultParent({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

type GroupedFields = {
  [groupId: string]: string[];
  ungrouped: string[];
};

// Width classes for grid layout (6-column grid for better 33%/50%/100% support)
const widthClasses: Record<string, string> = {
  '33': 'col-span-6 md:col-span-2',
  '50': 'col-span-6 md:col-span-3',
  '100': 'col-span-6',
};

export default function AutoFormObject<SchemaType extends z.ZodObject<any>>({
  schema,
  form,
  fieldConfig,
  path = [],
  dependencies = [],
  className,
  metadataSchema,
  disableGrouping = false,
}: {
  schema: SchemaType | z.ZodTypeAny;
  form: ReturnType<typeof useForm>;
  fieldConfig?: FieldConfig<z.infer<SchemaType>>;
  path?: string[];
  dependencies?: Dependency<z.infer<SchemaType>>[];
  className?: string;
  metadataSchema?: JsonSchemaType;
  disableGrouping?: boolean;
}) {
  const { watch, formState } = useFormContext();
  const { locale, t } = useIntl();
  // Use inline groups from the schema instead of global metadata-groups collection
  const inlineGroups: MetadataGroup[] = metadataSchema?.groups ?? [];

  // Track active tab state - will switch to first tab with errors on submit
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

  // Get schema shape (may be null)
  const objectSchema = schema ? getObjectFormSchema(schema) : null;
  const shape = objectSchema?.shape;

  // Get errors for a specific field path - defined early for use in effect
  const getFieldErrorsForEffect = (fieldName: string): boolean => {
    const fullPath = [...path, fieldName].join('.');
    const pathParts = fullPath.split('.');
    let current: Record<string, unknown> = formState.errors as Record<string, unknown>;
    for (const part of pathParts) {
      if (!current || typeof current !== 'object') return false;
      current = current[part] as Record<string, unknown>;
    }
    return current !== undefined && current !== null;
  };

  // Pre-compute grouped fields and error counts for the effect
  // This needs to be done before any early returns
  const precomputedGroupedFields: GroupedFields = { ungrouped: [] };
  const sortedGroupsForEffect = [...inlineGroups].sort((a, b) => a.order - b.order);

  // Initialize groups
  for (const group of sortedGroupsForEffect) {
    precomputedGroupedFields[group.id] = [];
  }

  // Group fields if we have shape and metadata
  if (shape && metadataSchema?.properties) {
    const fieldNames = metadataSchema.propertyOrder?.length
      ? metadataSchema.propertyOrder.filter((name) => Object.hasOwn(shape, name))
      : Object.keys(shape);

    for (const name of fieldNames) {
      const fieldMetadata = metadataSchema.properties[name];
      if (fieldMetadata && typeof fieldMetadata === 'object' && 'groupId' in fieldMetadata) {
        const groupId = fieldMetadata.groupId as string | null;
        if (groupId && precomputedGroupedFields[groupId] !== undefined) {
          precomputedGroupedFields[groupId].push(name);
        } else {
          precomputedGroupedFields.ungrouped.push(name);
        }
      } else {
        precomputedGroupedFields.ungrouped.push(name);
      }
    }
  }

  // Find first tab with errors (for auto-switching)
  const getGroupErrorCountForEffect = (fieldNames: string[]): number => {
    return fieldNames.filter((name) => getFieldErrorsForEffect(name)).length;
  };

  const firstTabWithErrors =
    formState.submitCount > 0 && sortedGroupsForEffect.length > 0
      ? sortedGroupsForEffect.find((g) => {
          const fields = precomputedGroupedFields[g.id] || [];
          return getGroupErrorCountForEffect(fields) > 0;
        })?.id ||
        (getGroupErrorCountForEffect(precomputedGroupedFields.ungrouped) > 0 ? 'ungrouped' : null)
      : null;

  // Auto-switch to first tab with errors after submit attempt
  useEffect(() => {
    if (firstTabWithErrors && formState.submitCount > 0) {
      setActiveTab(firstTabWithErrors);
    }
  }, [formState.submitCount, firstTabWithErrors]);

  // Early returns after hooks
  if (!schema || !shape) {
    return null;
  }

  const fieldNamesInOrder =
    metadataSchema?.propertyOrder && metadataSchema.propertyOrder.length > 0
      ? metadataSchema.propertyOrder.filter((name) => Object.hasOwn(shape, name))
      : Object.keys(shape);

  const handleIfZodNumber = (item: z.ZodAny) => {
    const base = getBaseSchema(item);
    if (base instanceof z.ZodNumber) {
      base._def.coerce = true;
    }
    return item;
  };

  const getCustomerAccess = (name: string, metadataSchema?: JsonSchemaType) => {
    if (!metadataSchema?.properties) return 'read';
    return (
      (metadataSchema.properties[name]?.customerAccess as 'none' | 'read' | 'read-and-write') ||
      'read'
    );
  };

  const getLocalizedLabel = (
    name: string,
    defaultLabel: string,
    metadataSchema?: JsonSchemaType
  ) => {
    if (!metadataSchema?.properties || !metadataSchema.properties[name]) {
      return defaultLabel;
    }

    const fieldDef = metadataSchema.properties[name];

    // If locale is English and we have an English translation, use it
    if (locale === 'en-US' && fieldDef.description_en && fieldDef.description_en.trim() !== '') {
      return fieldDef.description_en;
    }

    // Otherwise, use the German label (description)
    return fieldDef.description || defaultLabel;
  };

  const renderField = (name: string) => {
    let item = shape[name] as z.ZodAny;
    item = handleIfZodNumber(item) as z.ZodAny;
    const baseSchema = getBaseSchema(item);
    const zodBaseType = getBaseType(item);
    const defaultItemName = item.description ?? beautifyObjectName(name);
    const key = [...path, name].join('.');

    const {
      isHidden,
      isDisabled: baseIsDisabled,
      isRequired: isRequiredByDependency,
      overrideOptions,
    } = resolveDependencies(dependencies, name, watch);

    const globalFieldConfig = fieldConfig?.['*'] as FieldConfigItem & {
      metadataSchema?: JsonSchemaType;
      isCustomer?: boolean;
    };
    const isCustomer = globalFieldConfig?.isCustomer;
    const metadataSchema = globalFieldConfig?.metadataSchema;

    // Get the localized label based on current locale
    const itemName = getLocalizedLabel(name, defaultItemName, metadataSchema);

    // Handle customer access permissions
    let isDisabled = baseIsDisabled;
    if (isCustomer) {
      const customerAccess = getCustomerAccess(name, metadataSchema);
      if (customerAccess === 'none') {
        return null;
      }
      if (customerAccess === 'read') {
        isDisabled = true;
      }
    }

    if (isHidden) {
      return null;
    }

    if (baseSchema instanceof z.ZodObject) {
      return (
        <AccordionItem value={name} key={key} className="border-none">
          <AccordionTrigger>{itemName}</AccordionTrigger>
          <AccordionContent className="p-2">
            <AutoFormObject
              schema={baseSchema as unknown as z.ZodObject<any>}
              form={form}
              fieldConfig={(fieldConfig?.[name] ?? {}) as FieldConfig<z.infer<typeof item>>}
              path={[...path, name]}
              metadataSchema={metadataSchema}
              disableGrouping={disableGrouping}
            />
          </AccordionContent>
        </AccordionItem>
      );
    }
    if (baseSchema instanceof z.ZodArray) {
      return (
        <AutoFormArray
          key={key}
          name={name}
          item={baseSchema as unknown as z.ZodArray<any>}
          form={form}
          fieldConfig={fieldConfig?.[name] ?? {}}
          path={[...path, name]}
        />
      );
    }

    const fieldConfigItem: FieldConfigItem = {
      ...(fieldConfig?.['*'] ?? {}),
      ...(fieldConfig?.[name] ?? {}),
    };
    const zodInputProps = zodToHtmlInputProps(item);
    const isRequiredFromSchema = Boolean(metadataSchema?.required?.includes(name));
    const isRequired =
      isRequiredByDependency ||
      isRequiredFromSchema ||
      fieldConfigItem.inputProps?.required ||
      false;

    if (overrideOptions) {
      item = z.enum(overrideOptions) as unknown as z.ZodAny;
    }

    const inputType = fieldConfigItem.fieldType ?? DEFAULT_ZOD_HANDLERS[zodBaseType] ?? 'fallback';

    const InputComponent =
      typeof inputType === 'function' ? inputType : INPUT_COMPONENTS[inputType];

    if (!InputComponent) {
      return null;
    }

    const ParentElement = fieldConfigItem.renderParent ?? DefaultParent;

    return (
      <FormField
        control={form.control}
        name={key}
        key={key}
        render={({ field }) => {
          const defaultValue = fieldConfigItem.inputProps?.defaultValue;
          let value = field.value ?? defaultValue ?? '';
          if (baseSchema instanceof z.ZodNumber) {
            const isRequiredFromSchema = Boolean(metadataSchema?.required?.includes(name));
            if (typeof value === 'string') {
              const cleaned = value.replace(/[^\d.]/g, '');
              if (!isRequiredFromSchema && (cleaned === '' || Number.isNaN(Number(cleaned)))) {
                value = undefined as unknown as string;
              } else {
                value = cleaned as unknown as string;
              }
            }
          }
          if (baseSchema instanceof z.ZodDate) {
            const isRequiredFromSchema = Boolean(metadataSchema?.required?.includes(name));
            if (typeof value === 'string') {
              const parsed = new Date(value);
              if (!isRequiredFromSchema && Number.isNaN(parsed.getTime())) {
                value = undefined as unknown as string;
              } else if (!Number.isNaN(parsed.getTime())) {
                // keep as Date object for the DatePicker
                value = parsed as unknown as string;
              }
            } else if (value && !(value instanceof Date)) {
              // Unknown type, clear if optional
              if (!isRequiredFromSchema) {
                value = undefined as unknown as string;
              }
            }
          }

          const fieldProps = {
            ...zodInputProps,
            ...field,
            ...fieldConfigItem.inputProps,
            disabled: fieldConfigItem.inputProps?.disabled || isDisabled,
            ref: undefined,
            value: value,
            required: isRequired,
          };

          return (
            <ParentElement key={`${key}.parent`}>
              <InputComponent
                zodInputProps={zodInputProps}
                field={field}
                fieldConfigItem={fieldConfigItem}
                label={itemName}
                isRequired={isRequired}
                zodItem={item}
                fieldProps={fieldProps}
                className={fieldProps.className}
              />
            </ParentElement>
          );
        }}
      />
    );
  };

  // Get field width from metadata schema
  const getFieldWidth = (name: string): string => {
    if (!metadataSchema?.properties) return '100';
    const fieldDef = metadataSchema.properties[name];
    return fieldDef?.width || '100';
  };

  // Get errors for a specific field path
  const getFieldErrors = (fieldName: string): boolean => {
    const fullPath = [...path, fieldName].join('.');
    // Navigate through nested errors object
    const pathParts = fullPath.split('.');
    let current: Record<string, unknown> = formState.errors as Record<string, unknown>;
    for (const part of pathParts) {
      if (!current || typeof current !== 'object') return false;
      current = current[part] as Record<string, unknown>;
    }
    return current !== undefined && current !== null;
  };

  // Count errors for a group of fields
  const getGroupErrorCount = (fieldNames: string[]): number => {
    return fieldNames.filter((name) => getFieldErrors(name)).length;
  };

  // Check if field is a section (visual separator)
  const isSection = (name: string): boolean => {
    if (!metadataSchema?.properties) return false;
    const fieldDef = metadataSchema.properties[name];
    return fieldDef?.type === 'section';
  };

  // Render section field (visual separator)
  const renderSection = (name: string) => {
    const defaultLabel = beautifyObjectName(name);
    const label = getLocalizedLabel(name, defaultLabel, metadataSchema);

    return (
      <div key={name} className="col-span-6 mt-4 mb-2 pb-2 border-b border-border">
        <h4 className="text-sm font-semibold text-foreground">{label}</h4>
      </div>
    );
  };

  // Render field with width support
  const renderFieldWithWidth = (name: string) => {
    // Handle section fields specially
    if (isSection(name)) {
      return renderSection(name);
    }

    const width = getFieldWidth(name);
    const widthClass = widthClasses[width] || 'col-span-6';

    const field = renderField(name);
    if (!field) return null;

    return (
      <div key={`${name}-wrapper`} className={cn(widthClass)}>
        {field}
      </div>
    );
  };

  if (disableGrouping) {
    return (
      <div className={cn('grid grid-cols-6 gap-4', className)}>
        {fieldNamesInOrder.map(renderFieldWithWidth)}
      </div>
    );
  }

  // Group fields based on metadata schema using inline groups
  const groupedFields: GroupedFields = {
    ungrouped: [],
  };

  // Initialize groups from inline groups
  for (const group of inlineGroups) {
    groupedFields[group.id] = [];
  }

  if (metadataSchema?.properties) {
    fieldNamesInOrder.forEach((name) => {
      const fieldMetadata = metadataSchema.properties[name];
      if (fieldMetadata && typeof fieldMetadata === 'object' && 'groupId' in fieldMetadata) {
        const groupId = fieldMetadata.groupId as string | null;
        if (groupId && groupedFields[groupId] !== undefined) {
          groupedFields[groupId].push(name);
        } else {
          groupedFields.ungrouped.push(name);
        }
      } else {
        groupedFields.ungrouped.push(name);
      }
    });
  } else {
    groupedFields.ungrouped = [...fieldNamesInOrder];
  }

  // Sort groups by order
  const sortedGroups = [...inlineGroups].sort((a, b) => a.order - b.order);

  // Calculate default tab (reuse precomputed values for consistency with effect)
  const firstGroupWithFields = sortedGroups.find((g) => (groupedFields[g.id]?.length || 0) > 0);
  const defaultTab = firstGroupWithFields?.id || sortedGroups[0]?.id || 'ungrouped';
  const currentTab = activeTab || defaultTab;

  // If we have inline groups, render as tabs
  if (sortedGroups.length > 0) {
    return (
      <Tabs
        value={currentTab}
        onValueChange={setActiveTab}
        className={cn('flex flex-col -mt-2 -mx-5', className)}
      >
        {/* Tabs - full-width border line */}
        <div className="h-10 border-b px-6 flex items-end">
          <TabsList className="h-auto bg-transparent p-0 gap-4 -mb-px">
            {sortedGroups.map((group) => {
              const fields = groupedFields[group.id] || [];
              const fieldCount = fields.length;
              if (fieldCount === 0) return null;
              const errorCount = getGroupErrorCount(fields);
              const hasErrors = errorCount > 0;
              const label = locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe;
              return (
                <TabsTrigger
                  key={group.id}
                  value={group.id}
                  className={cn(
                    'px-0 pb-2 pt-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary gap-1.5',
                    hasErrors && 'text-destructive data-[state=active]:border-destructive'
                  )}
                >
                  {label}
                  {hasErrors && <AlertCircle className="size-3.5" />}
                </TabsTrigger>
              );
            })}
            {groupedFields.ungrouped.length > 0 && (
              <TabsTrigger
                value="ungrouped"
                className={cn(
                  'px-0 pb-2 pt-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary gap-1.5',
                  getGroupErrorCount(groupedFields.ungrouped) > 0 &&
                    'text-destructive data-[state=active]:border-destructive'
                )}
              >
                {t('metadata.builder.ungrouped')}
                {getGroupErrorCount(groupedFields.ungrouped) > 0 && (
                  <AlertCircle className="size-3.5" />
                )}
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {sortedGroups.map((group) => {
          const fields = groupedFields[group.id] || [];
          if (fields.length === 0) return null;
          return (
            <TabsContent key={group.id} value={group.id} className="m-0 px-6 pt-4">
              <div className="grid grid-cols-6 gap-4">{fields.map(renderFieldWithWidth)}</div>
            </TabsContent>
          );
        })}

        {groupedFields.ungrouped.length > 0 && (
          <TabsContent value="ungrouped" className="m-0 px-6 pt-4">
            <div className="grid grid-cols-6 gap-4">
              {groupedFields.ungrouped.map(renderFieldWithWidth)}
            </div>
          </TabsContent>
        )}
      </Tabs>
    );
  }

  // Fallback: render without tabs (old accordion style for backward compatibility)
  return (
    <Accordion type="multiple" className={cn('space-y-5 border-none', className)}>
      {Object.entries(groupedFields).map(([groupId, fields]) => {
        if (fields.length === 0) return null;

        // Get the appropriate group name based on locale
        let displayGroupName = groupId;
        const groupObject = inlineGroups.find((g) => g.id === groupId);

        if (groupObject) {
          displayGroupName =
            locale === 'en-US' && groupObject.labelEn ? groupObject.labelEn : groupObject.labelDe;
        }

        return (
          <div key={groupId} className="space-y-4">
            {groupId !== 'ungrouped' && (
              <>
                <Label>{displayGroupName}</Label>
                <div className="relative">
                  <div className="absolute left-2 inset-y-0 w-px bg-muted" />
                  <div className="grid grid-cols-6 gap-4 pl-8">
                    {fields.map(renderFieldWithWidth)}
                  </div>
                </div>
              </>
            )}
            {groupId === 'ungrouped' && (
              <div className="grid grid-cols-6 gap-4">{fields.map(renderFieldWithWidth)}</div>
            )}
          </div>
        );
      })}
    </Accordion>
  );
}
